/**
 * Source of truth for the KPOW module's red ↔ blue political color palette.
 * All charts, gauges, and breakdown components import from here so a tweak
 * lands in one place instead of seven hex literals across the module.
 */
import { FLAT_THRESHOLD, signedToPercent } from './kpowFormat';

/** Saturated colors used by the chart line gradient + value pills + composition bars. */
export const KPOW_RED = '#ff3333';
export const KPOW_BLUE = '#265cff';

/** Independent / Vacant. */
export const KPOW_YELLOW = '#FACC15';
export const KPOW_GREY = '#B2B2B2';

/** Neutral midpoint, used for the chart's "0" axis tick (matches --purple-x10 in light mode). */
export const KPOW_PURPLE = '#aa00ff';

/** RGB tuples for the saturated palette. Useful for runtime interpolation. */
export const KPOW_RED_RGB: readonly [number, number, number] = [255, 51, 51];
export const KPOW_BLUE_RGB: readonly [number, number, number] = [38, 92, 255];

/** Linear interpolation `0 → KPOW_RED` to `1 → KPOW_BLUE`, returning an rgb() string. */
export const mixRedBlue = (t: number): string => {
    const clamped = Math.max(0, Math.min(1, t));
    const mix = (a: number, b: number): number => Math.round(a + (b - a) * clamped);
    return `rgb(${mix(KPOW_RED_RGB[0], KPOW_BLUE_RGB[0])}, ${mix(KPOW_RED_RGB[1], KPOW_BLUE_RGB[1])}, ${mix(KPOW_RED_RGB[2], KPOW_BLUE_RGB[2])})`;
};

/**
 * Shared coloring rule for the index's signed value (-50 D .. +50 R): solid red
 * above +5, solid blue below -5, and a red↔blue blend only across the band in
 * between. The chart line, gauge, and composition bars all key off this so the
 * threshold lives in exactly one place.
 */
export const KPOW_COLOR_THRESHOLD = 10;

/**
 * Party color from a signed value's sign: red toward R, blue toward D, undefined when within the
 * flat band (neutral). Used for deltas (24h change) and window-extreme stat values.
 */
export const partyColorBySign = (signed: number): string | undefined => {
    if (signed > FLAT_THRESHOLD) return KPOW_RED;
    if (signed < -FLAT_THRESHOLD) return KPOW_BLUE;
    return undefined;
};

/**
 * Discrete party color under the Midterms treatment: red above the tie line, blue
 * below, purple exactly on it — strict sign, with none of `partyColorBySign`'s flat
 * band or `signedColor`'s blend.
 */
export const midtermsPartyColor = (signed: number): string => {
    if (signed > 0) return KPOW_RED;
    if (signed < 0) return KPOW_BLUE;
    return KPOW_PURPLE;
};

/** Discrete color for a single signed value under the threshold rule (chart line/dot, gauge pill). */
export const signedColor = (signed: number): string => {
    if (signed >= KPOW_COLOR_THRESHOLD) return KPOW_RED;
    if (signed <= -KPOW_COLOR_THRESHOLD) return KPOW_BLUE;
    // Map [+threshold .. -threshold] → mixRedBlue's [0 (red) .. 1 (blue)].
    return mixRedBlue((KPOW_COLOR_THRESHOLD - signed) / (2 * KPOW_COLOR_THRESHOLD));
};

/**
 * Horizontal D→R reference track (blue left, red right) that stays solid outside
 * +/-KPOW_COLOR_THRESHOLD and only blends across the band in between — the CSS
 * analogue of the chart line's gradient, shared by the gauge + composition bars.
 */
export const redBlueTrackGradient = (): string => {
    const lowPct = signedToPercent(-KPOW_COLOR_THRESHOLD);
    const highPct = signedToPercent(KPOW_COLOR_THRESHOLD);
    return `linear-gradient(to right, ${KPOW_BLUE} 0%, ${KPOW_BLUE} ${lowPct}%, ${KPOW_RED} ${highPct}%, ${KPOW_RED} 100%)`;
};
